import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShoppingCart, AlertTriangle, MapPin, Tag, CreditCard, Eye } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import clsx from 'clsx';
import { getCheckoutInsights } from '../api/client';
import { useCurrentSite } from '../hooks/useCurrentSite';

const RANGES = [
  { value: '1d', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

function StatCard({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'neutral' | 'warning' | 'success';
}) {
  const toneClasses =
    tone === 'warning'
      ? 'border-amber-200 bg-amber-50'
      : tone === 'success'
        ? 'border-emerald-200 bg-emerald-50'
        : 'border-gray-200 bg-white';
  return (
    <div className={clsx('rounded-xl border p-4 shadow-sm', toneClasses)}>
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

export default function CheckoutInsightsPage() {
  const { siteId } = useCurrentSite();
  const [dateRange, setDateRange] = useState<string>('7d');

  const { data, isLoading, error } = useQuery({
    queryKey: ['checkout-insights', siteId, dateRange],
    queryFn: () => getCheckoutInsights({ siteId: siteId!, dateRange }),
    enabled: !!siteId,
  });

  if (!siteId) {
    return (
      <div className="card p-6 text-sm text-gray-500">Select a site to view checkout insights.</div>
    );
  }

  if (isLoading) {
    return (
      <div className="card flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card border-red-200 bg-red-50 p-6 text-sm text-red-700">
        Couldn't load checkout insights. {error instanceof Error ? error.message : ''}
      </div>
    );
  }

  const overallCvr =
    data.totalSessions > 0
      ? Math.round(
          ((data.funnel[data.funnel.length - 1]?.count ?? 0) / data.totalSessions) * 10000,
        ) / 100
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-brand-100 p-2 text-brand-700">
            <ShoppingCart className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Checkout Insights</h1>
            <p className="text-sm text-gray-500">
              Where customers leave checkout — across {data.totalSessions.toLocaleString()} sessions
            </p>
          </div>
        </div>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDateRange(r.value)}
              className={clsx(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                dateRange === r.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Sessions" value={data.totalSessions.toLocaleString()} />
        <StatCard
          label="Conversion rate"
          value={`${overallCvr}%`}
          tone={overallCvr >= 20 ? 'success' : overallCvr >= 10 ? 'neutral' : 'warning'}
        />
        <StatCard
          label="Unserviceable postcodes"
          value={data.friction.unserviceablePostcode}
          tone={data.friction.unserviceablePostcode > 0 ? 'warning' : 'neutral'}
          sub="Sessions hit a delivery dead-end"
        />
        <StatCard
          label="Payment failures"
          value={data.friction.paymentFailed}
          tone={data.friction.paymentFailed > 0 ? 'warning' : 'neutral'}
          sub={
            data.friction.paymentSubmitted > 0
              ? `of ${data.friction.paymentSubmitted.toLocaleString()} sessions that submitted payment`
              : undefined
          }
        />
      </div>

      {/* Funnel */}
      <div className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Funnel</h2>
        <p className="mb-4 text-xs text-gray-500">
          Sessions that reached each step in order. Drop-off is the percentage lost from the previous step.
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data.funnel} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(v: number, _name, payload) => [
                `${v.toLocaleString()} sessions (${payload.payload.percentage}%)`,
                'Count',
              ]}
            />
            <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-5">
          {data.funnel.map((step, idx) => (
            <div key={step.key} className="rounded-lg border border-gray-200 px-3 py-2">
              <div className="truncate text-xs font-medium text-gray-700">{step.label}</div>
              <div className="text-sm font-semibold text-gray-900">{step.count.toLocaleString()}</div>
              {idx > 0 && step.dropoff !== undefined && (
                <div
                  className={clsx(
                    'text-xs',
                    step.dropoff > 50 ? 'text-red-600' : step.dropoff > 25 ? 'text-amber-600' : 'text-gray-500',
                  )}
                >
                  −{step.dropoff}% from previous
                </div>
              )}
            </div>
          ))}
        </div>
        {(data.stepTimings ?? []).some((t) => t.avgMs !== null) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {(data.stepTimings ?? []).map((t) => (
              <div
                key={t.step}
                className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600"
                title={`${t.completions.toLocaleString()} completed step transitions`}
              >
                <span className="font-medium text-gray-800">{t.label}:</span>{' '}
                {t.avgMs !== null ? `${formatDuration(t.avgMs)} avg` : 'no data'}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Exit clusters + Friction */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card p-6">
          <h2 className="mb-1 text-lg font-semibold text-gray-900">Where abandoners last were</h2>
          <p className="mb-4 text-xs text-gray-500">
            The step abandoned sessions last touched before leaving.
            {(data.friction.tabSwitchReturns ?? 0) > 0 && (
              <>
                {' '}
                {data.friction.tabSwitchReturns.toLocaleString()} session
                {data.friction.tabSwitchReturns === 1 ? '' : 's'} tab-switched away and came back
                (e.g. 3DS/banking app) — those are netted out of exit triggers.
              </>
            )}
          </p>
          {data.exitClusters.length === 0 ? (
            <p className="text-sm text-gray-500">No abandonment data yet.</p>
          ) : (
            <div className="space-y-2">
              {data.exitClusters.map((c) => {
                const max = Math.max(...data.exitClusters.map((x) => x.count));
                const width = max > 0 ? (c.count / max) * 100 : 0;
                return (
                  <div key={c.step}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-medium text-gray-700">{c.step}</span>
                      <span className="text-gray-500">{c.count.toLocaleString()}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-amber-400"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card p-6">
          <h2 className="mb-1 text-lg font-semibold text-gray-900">Friction signals</h2>
          <p className="mb-4 text-xs text-gray-500">
            Specific known dead-ends in this period.
          </p>
          <div className="space-y-3">
            <FrictionRow
              icon={<MapPin className="h-4 w-4 text-amber-600" />}
              label="Unserviceable postcode"
              count={data.friction.unserviceablePostcode}
              hint="Customer entered a postcode you don't deliver to"
              breakdown={data.friction.topUnserviceablePostcodes}
            />
            <FrictionRow
              icon={<Tag className="h-4 w-4 text-amber-600" />}
              label="Promo code rejected"
              count={data.friction.promoFailed}
              hint="Failed promo applies — invalid, expired, ineligible"
              breakdown={data.friction.topPromoFailReasons}
            />
            <FrictionRow
              icon={<CreditCard className="h-4 w-4 text-red-600" />}
              label="Payment failed"
              count={data.friction.paymentFailed}
              hint="Stripe declined or processing error"
              breakdown={data.friction.topDeclineCodes}
            />
          </div>
        </div>
      </div>

      {/* Variant + Device */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ScoreTable title="Variant" rows={data.variants.map((v) => ({ key: v.variant, ...v }))} keyLabel="Variant" />
        <ScoreTable title="Device" rows={data.devices.map((d) => ({ key: d.device, views: d.views, completions: d.completions, cvr: d.cvr }))} keyLabel="Device" />
      </div>

      {/* Recent abandoned sessions */}
      <div className="card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Recent abandoned sessions</h2>
            <p className="text-xs text-gray-500">
              Click "Watch" to open the replay and see exactly what happened.
            </p>
          </div>
        </div>
        {data.recentAbandoned.length === 0 ? (
          <p className="text-sm text-gray-500">No abandoned sessions in this window.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">When</th>
                  <th className="table-header">Last step</th>
                  <th className="table-header">Variant</th>
                  <th className="table-header">Device</th>
                  <th className="table-header">Exit</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.recentAbandoned.map((s) => (
                  <tr key={s.sessionId} className="hover:bg-gray-50">
                    <td className="table-cell text-gray-700">
                      {new Date(s.startedAt).toLocaleString()}
                    </td>
                    <td className="table-cell">
                      {s.lastStep === 0
                        ? 'Account'
                        : s.lastStep === 1
                          ? 'Delivery'
                          : s.lastStep === 2
                            ? 'Payment'
                            : '—'}
                    </td>
                    <td className="table-cell text-gray-600">{s.variant ?? '—'}</td>
                    <td className="table-cell capitalize text-gray-600">{s.device ?? '—'}</td>
                    <td className="table-cell text-xs text-gray-500">
                      {s.exitTrigger === 'unload'
                        ? 'Closed page'
                        : s.exitTrigger === 'visibility_hidden'
                          ? 'Tab hidden'
                          : s.exitTrigger === 'navigation'
                            ? 'Navigated away'
                            : '—'}
                      {s.returnedAfterExit && (
                        <span
                          className="ml-1 rounded bg-blue-50 px-1 py-0.5 text-[10px] font-medium text-blue-600"
                          title="Left and came back at least once (tab switch) before finally abandoning"
                        >
                          returned
                        </span>
                      )}
                    </td>
                    <td className="table-cell">
                      <Link
                        to={`/sessions/${s.sessionId}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100"
                      >
                        <Eye className="h-3 w-3" />
                        Watch
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs % 60}s`;
}

function FrictionRow({
  icon,
  label,
  count,
  hint,
  breakdown,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  hint: string;
  breakdown?: Array<{ value: string; count: number }>;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">{label}</span>
          <span
            className={clsx(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500',
            )}
          >
            {count.toLocaleString()}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">{hint}</p>
        {(breakdown ?? []).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(breakdown ?? []).slice(0, 6).map((b) => (
              <span
                key={b.value}
                className="rounded bg-white px-1.5 py-0.5 text-[11px] text-gray-600 ring-1 ring-gray-200"
              >
                {b.value} × {b.count}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreTable({
  title,
  rows,
  keyLabel,
}: {
  title: string;
  keyLabel: string;
  rows: Array<{ key: string; views: number; completions: number; cvr: number }>;
}) {
  return (
    <div className="card p-6">
      <h2 className="mb-3 text-lg font-semibold text-gray-900">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No data yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="table-header">{keyLabel}</th>
              <th className="table-header text-right">Views</th>
              <th className="table-header text-right">Purchases</th>
              <th className="table-header text-right">CVR</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="table-cell capitalize text-gray-900">{r.key}</td>
                <td className="table-cell text-right text-gray-600">{r.views.toLocaleString()}</td>
                <td className="table-cell text-right text-gray-600">{r.completions.toLocaleString()}</td>
                <td className="table-cell text-right">
                  <span
                    className={clsx(
                      'rounded-md px-2 py-0.5 text-xs font-semibold',
                      r.cvr >= 20
                        ? 'bg-emerald-100 text-emerald-700'
                        : r.cvr >= 10
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-amber-100 text-amber-700',
                    )}
                  >
                    {r.cvr}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
